import { useAuth } from '@/contexts/AuthContext';
import { formatAUD, getFinancialYear, getFinancialYearStart, getFinancialYearEnd } from '@/lib/format';
import { Card, CardContent } from '@/components/ui/card';
import { CalendarDays, TrendingUp, TrendingDown, Receipt, ArrowRight } from 'lucide-react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Progress } from '@/components/ui/progress';

export function YearInReviewWidget() {
  const { data } = useAuth();
  const navigate = useNavigate();
  const now = new Date();
  const fy = getFinancialYear(now);
  const fyStart = getFinancialYearStart(now);
  const fyEnd = getFinancialYearEnd(now);

  const stats = useMemo(() => {
    const txns = data.transactions.filter(t => {
      const d = new Date(t.date);
      return d >= fyStart && d <= fyEnd;
    });
    const income = txns.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);
    const expenses = txns.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);
    const deductible = txns.filter(t => t.taxDeductible).reduce((s, t) => s + t.amount, 0);
    const savingsRate = income > 0 ? ((income - expenses) / income) * 100 : 0;

    // FY progress (how far through the year)
    const totalDays = (fyEnd.getTime() - fyStart.getTime()) / (1000 * 60 * 60 * 24);
    const elapsed = Math.max(0, (now.getTime() - fyStart.getTime()) / (1000 * 60 * 60 * 24));
    const progress = Math.min((elapsed / totalDays) * 100, 100);

    return { income, expenses, deductible, savingsRate, progress, txnCount: txns.length };
  }, [data.transactions, fyStart, fyEnd]);

  return (
    <Card
      className="cursor-pointer hover:bg-accent/50 transition-colors"
      onClick={() => navigate('/more/year-in-review')}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold">FY {fy} Snapshot</h3>
          </div>
          <span className="text-xs text-primary font-medium flex items-center gap-0.5">
            Full Review <ArrowRight className="h-3 w-3" />
          </span>
        </div>

        {/* FY progress bar */}
        <div className="mb-3">
          <div className="flex justify-between text-xs text-muted-foreground mb-1">
            <span>Year progress</span>
            <span>{Math.round(stats.progress)}%</span>
          </div>
          <Progress value={stats.progress} className="h-1.5" />
        </div>

        {/* Key numbers */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
          <div className="flex items-center gap-1.5">
            <TrendingUp className="h-3 w-3 text-success" />
            <span className="text-muted-foreground">Income</span>
          </div>
          <span className="text-right font-mono font-semibold text-success">{formatAUD(stats.income)}</span>

          <div className="flex items-center gap-1.5">
            <TrendingDown className="h-3 w-3 text-destructive" />
            <span className="text-muted-foreground">Expenses</span>
          </div>
          <span className="text-right font-mono font-semibold text-destructive">{formatAUD(stats.expenses)}</span>

          <div className="flex items-center gap-1.5">
            <Receipt className="h-3 w-3 text-primary" />
            <span className="text-muted-foreground">Deductions</span>
          </div>
          <span className="text-right font-mono font-semibold text-primary">{formatAUD(stats.deductible)}</span>
        </div>

        {/* Savings rate badge */}
        <div className="mt-3 flex items-center justify-between rounded-lg bg-muted/50 px-3 py-1.5">
          <span className="text-xs text-muted-foreground">Savings rate</span>
          <span className={cn(
            'text-xs font-bold',
            stats.savingsRate >= 20 ? 'text-success' : stats.savingsRate >= 0 ? 'text-foreground' : 'text-destructive'
          )}>
            {stats.savingsRate.toFixed(1)}%
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
